import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardPlus, MessageSquare, Shield, UserPlus, Users } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Loader } from "@/components/ui/Loader";
import { ROUTES } from "@/config/constants";
import { formatPct } from "@/lib/gente-gestao/format";
import { extractGgError, fetchGgResumo, type GgResumoDto } from "@/services/genteGestao";

export function GenteGestaoVisaoPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<GgResumoDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchGgResumo()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(extractGgError(e, "Não foi possível carregar o resumo."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Loader size="lg" label="Carregando visão geral..." />
      </div>
    );
  }

  if (error) {
    return <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card title="Colaboradores ativos" value={String(data?.colaboradoresAtivos ?? 0)} icon={<Users className="h-5 w-5" />} />
        <Card title="Pesquisas ativas" value={String(data?.pesquisasAtivas ?? 0)} />
        <Card
          title="eNPS"
          value={data?.enps == null ? "—" : String(data.enps)}
          subtitle={data?.pesquisa ? data.pesquisa.titulo : "Nenhuma pesquisa com resultados"}
        />
        <Card
          title="Adesão (pesquisa ativa/recente)"
          value={formatPct(data?.adesao ?? null, 0)}
        />
        <Card title="Ciclos de liderança ativos" value={String(data?.ciclosAtivos ?? 0)} icon={<Shield className="h-5 w-5" />} />
        <Card title="Pares pendentes" value={String(data?.paresPendentes ?? 0)} subtitle="Convites sem resposta" />
        <Card title="Líderes com nota visível" value={String(data?.lideresComNota ?? 0)} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => navigate(ROUTES.GENTE_GESTAO_COLABORADORES)}>
          Cadastrar colaborador
        </Button>
        <Button
          variant="secondary"
          leftIcon={<ClipboardPlus className="h-4 w-4" />}
          onClick={() => navigate(ROUTES.GENTE_GESTAO_NPS)}
        >
          Criar pesquisa
        </Button>
        <Button
          variant="secondary"
          leftIcon={<Shield className="h-4 w-4" />}
          onClick={() => navigate(ROUTES.GENTE_GESTAO_LIDERANCA)}
        >
          Avaliação de liderança
        </Button>
        <Button
          variant="secondary"
          leftIcon={<MessageSquare className="h-4 w-4" />}
          onClick={() => navigate(ROUTES.GENTE_GESTAO_ESCUTA)}
        >
          Canal de escuta
        </Button>
      </div>
    </div>
  );
}
